import { useState, useRef } from "react";
import { Upload, Asterisk, FileText, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FileTypeEnum, FileUpload as FileUploadType } from "../types";
import { formApi } from "../api/form";

interface FileUploadProps {
  type: FileTypeEnum;
  label: string;
  files: FileUploadType[];
  onFilesChange: (files: FileUploadType[]) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  token?: string; // Token for viewing uploaded files
}

export const FileUpload: React.FC<FileUploadProps> = ({
  type,
  label,
  files,
  onFilesChange,
  disabled = false,
  required = false,
  error,
  token,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: FileUploadType[] = Array.from(selectedFiles).map((file) => ({
      file,
      type,
      uploading: false,
    }));
    onFilesChange([...files, ...newFiles]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileView = async (fileUpload: FileUploadType, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the upload area click

    // If file is not uploaded yet, don't do anything
    if (!fileUpload.fileMetadata) {
      return;
    }

    const fileKey = fileUpload.fileMetadata?.file_key;
    if (!fileKey || !token) {
      return;
    }

    try {
      setLoadingFile(fileKey);
      const { view_url } = await formApi.getViewUrl(token, { file_key: fileKey });
      // Open file in new tab
      window.open(view_url, "_blank");
    } catch (error) {
      console.error("Error viewing file:", error);
      alert(t("fileUpload.viewError") || "Error opening file. Please try again.");
    } finally {
      setLoadingFile(null);
    }
  };

  return (
    <div className="flex flex-col gap-[8px] w-full">
      <div className="flex items-center gap-[4px]">
        <label className="font-bold text-off-black text-base">{label}</label>
        {required && <Asterisk className="w-[8px] h-[8px] text-off-red" />}
      </div>

      <div
        className={`
          bg-off-white rounded-[16px] h-[112px] flex flex-col items-center justify-center gap-[9px]
          cursor-pointer transition-all
          ${isDragging ? "ring-2 ring-off-red" : ""}
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <div className="bg-off-red rounded-[20px] w-10 h-10 flex items-center justify-center p-2">
          <Upload className="w-6 h-6 text-white" />
        </div>
        <p className="text-off-red text-base text-center underline">
          {t("fileUpload.chooseFiles")}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          disabled={disabled}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((fileUpload, index) => {
            // Check if this is a restored file (has metadata but no actual file data)
            const isRestoredFile = fileUpload.fileMetadata && fileUpload.file.size === 0;

            // Get the display name: prioritize file.name (original filename), then metadata, then extract from file_key
            let fileName: string;
            // First priority: use the original file.name if it exists and is not empty
            if (fileUpload.file.name && fileUpload.file.name.trim() !== "") {
              fileName = fileUpload.file.name;
            }
            // Second priority: use original_filename from metadata if available
            else if (fileUpload.fileMetadata?.metadata?.original_filename) {
              fileName = fileUpload.fileMetadata.metadata.original_filename as string;
            }
            // Third priority: extract from file_key (but clean it up)
            else if (fileUpload.fileMetadata?.file_key) {
              const keyParts = fileUpload.fileMetadata.file_key.split("/");
              const lastPart = keyParts[keyParts.length - 1] || "Uploaded file";
              // Remove the UUID prefix if present (format: type_uuid.ext)
              const cleanedName = lastPart.replace(/^[^_]+_[a-f0-9-]+\./, "") || lastPart;
              fileName = cleanedName !== lastPart ? cleanedName : lastPart;
            }
            // Fallback
            else {
              fileName = "Uploaded file";
            }

            // Get file size: prefer from metadata, then from file object
            const fileSize =
              isRestoredFile && fileUpload.fileMetadata?.metadata?.file_size
                ? (fileUpload.fileMetadata.metadata.file_size as number)
                : fileUpload.file.size;

            const canViewFile = fileUpload.fileMetadata && token && !fileUpload.uploading;
            const isFileLoading = loadingFile === fileUpload.fileMetadata?.file_key;

            return (
              <div
                key={index}
                className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-300 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-off-red/10 rounded-lg flex items-center justify-center">
                      {canViewFile ? (
                        <FileText className="w-5 h-5 text-off-red" />
                      ) : (
                        <Upload className="w-5 h-5 text-off-red" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    {canViewFile ? (
                      <button
                        type="button"
                        onClick={(e) => handleFileView(fileUpload, e)}
                        disabled={isFileLoading}
                        className="text-sm font-medium text-off-black block truncate text-left hover:text-off-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
                        title={
                          isFileLoading
                            ? t("fileUpload.loading") || "Loading..."
                            : `${t("fileUpload.view") || "View"} ${fileName}`
                        }
                      >
                        {isFileLoading ? (
                          <span className="text-blue-600 truncate block">
                            {t("fileUpload.loading") || "Loading..."}
                          </span>
                        ) : (
                          <span className="underline hover:no-underline truncate block">
                            {fileName}
                          </span>
                        )}
                      </button>
                    ) : (
                      <span
                        className="text-sm font-medium text-off-black block truncate"
                        title={fileName}
                      >
                        {fileName}
                      </span>
                    )}
                    {fileSize > 0 && (
                      <span className="text-xs text-off-grey">
                        {(fileSize / 1024).toFixed(2)} KB
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {fileUpload.uploading && (
                      <span className="text-xs text-blue-600 whitespace-nowrap animate-pulse">
                        {t("fileUpload.uploading")}
                      </span>
                    )}
                    {fileUpload.error && (
                      <span className="text-xs text-red-600 whitespace-nowrap">
                        {fileUpload.error}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={fileUpload.uploading || disabled}
                  aria-label={t("fileUpload.remove")}
                  className="ml-2 text-off-red hover:text-off-red/80 disabled:opacity-50 flex-shrink-0 p-1.5 rounded hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};
